import React from 'react'
import PropTypes from 'prop-types'

import Popover from 'components/popover'
import TrackOptionsMenu from './TrackOptionsMenu'
import TrackPlaylistMenu from './TrackPlaylistMenu'

/**
 * @desc Track options top-level component
 * @memberof components/playlist/PlaylistTrackOptions
 *
 * @param  {Object} track              				[Track for which the menu is rendered]
 * @param  {[Playlist]} playlists                   [List of available playlists]
 * @param  {Boolean} isDownloadAllowed              [Show 'Download' option]
 *
 * @param  {Function} onToggle               		[Dropdown toggle handler]
 * @param  {Function} onRemoveClick                 ['Remove' option click handler]
 * @param  {Function} onDownloadClick               [(Optional) 'Download' option click handler]
 * @param  {Function} onPlayNextClick               ['Play next' option click handler]
 * @param  {Function} onPlayLaterClick              ['Play later' option click handler]
 * @param  {Function} onCreatePlaylist         		['Create playlist' option click handler]
 * @param  {Function} onAddTrackToPlaylist          [Playlist to add selection handler]
 *
 * @type {Component}
 */
export default class PlaylistTrackOptions extends React.PureComponent {
  static propTypes = {
    track: PropTypes.object,
    playlists: PropTypes.arrayOf(PropTypes.object),
    isDownloadAllowed: PropTypes.bool,
    onToggle: PropTypes.func,
    onDownloadClick: PropTypes.func,
    onRemoveClick: PropTypes.func.isRequired,
    onPlayNextClick: PropTypes.func.isRequired,
    onPlayLaterClick: PropTypes.func.isRequired,
    onCreatePlaylist: PropTypes.func.isRequired,
    onAddTrackToPlaylist: PropTypes.func.isRequired
  }
  static defaultProps = {
    isDownloadAllowed: false
  }

  state = {
    showPlaylistMenu: false
  }

  /* Event Handlers */

  handleAddToPlaylistClick = () => this.setState({ showPlaylistMenu: true })

  handleDownloadClick = (format) => {
    const { track, onDownloadClick } = this.props
    return onDownloadClick(track, format)
  }

  handleRemoveClick = () => {
    const { track, onRemoveClick } = this.props
    return onRemoveClick(track)
  }

  handlePlayNextClick = () => {
    const { track, onPlayNextClick } = this.props
    return onPlayNextClick(track)
  }

  handlePlayLaterClick = () => {
    const { track, onPlayLaterClick } = this.props
    return onPlayLaterClick(track)
  }

  handlePlaylistClick = (playlist) => {
    const { track, onToggle, onAddTrackToPlaylist } = this.props

    onAddTrackToPlaylist(playlist, track)
    onToggle()
  }

  handleCreatePlaylistClick = (e) => {
    e.stopPropagation()
    const { track, onToggle, onCreatePlaylist } = this.props

    onCreatePlaylist(track)
    onToggle()
  }

  handleToggle = () => {
    const { onToggle } = this.props
    if (typeof onToggle === 'function') onToggle()

    this.setState({ showPlaylistMenu: false })
  }

  /* Rendering */

  renderOptionsMenu = (isDownloadAllowed) => (
    <TrackOptionsMenu
      isDownloadAllowed={isDownloadAllowed}
      onRemoveClick={this.handleRemoveClick}
      onDownloadClick={this.handleDownloadClick}
      onAddToPlaylistClick={this.handleAddToPlaylistClick}
    />
  )

  renderPlaylistMenu = (playlists) => (
    <TrackPlaylistMenu
      playlists={playlists}
      onPlaylistClick={this.handlePlaylistClick}
      onPlayNextClick={this.handlePlayNextClick}
      onPlayLaterClick={this.handlePlayLaterClick}
      onCreatePlaylistClick={this.handleCreatePlaylistClick}
    />
  )

  render() {
    // Destructure to remove extraneous props from ...rest
    const {
      playlists,
      isDownloadAllowed,
      onToggle,
      onDownloadClick,
      onPlayNextClick,
      onPlayLaterClick,
      ...rest
    } = this.props

    const { showPlaylistMenu } = this.state

    return (
      <Popover key={showPlaylistMenu} onToggle={this.handleToggle} {...rest}>
        {showPlaylistMenu
          ? this.renderPlaylistMenu(playlists)
          : this.renderOptionsMenu(isDownloadAllowed)}
      </Popover>
    )
  }
}
